"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DefaultAMMAPI = void 0;
const monetary_js_1 = require("@interlay/monetary-js");
const utils_1 = require("../utils");
const big_js_1 = __importDefault(require("big.js"));
const amm_1 = require("./amm/");
const HOP_LIMIT = 4;
const FEE_MULTIPLIER_STANDARD = 10000;
const FEE_MULTIPLIER_STABLE = 10000000000;
class DefaultAMMAPI {
    constructor(api, tokensAPI, transactionAPI) {
        this.api = api;
        this.tokensAPI = tokensAPI;
        this.transactionAPI = transactionAPI;
    }
    static getStablePoolInfo(poolData) {
        if (poolData.isBase) {
            return poolData.asBase;
        }
        if (poolData.isMeta) {
            return poolData.asMeta.info;
        }
        return null;
    }
    static getStableLpTokenFromPoolData(poolId, basePoolData) {
        const [ticker, decimals] = [
            utils_1.decodeBytesAsString(basePoolData.lpCurrencySymbol),
            basePoolData.lpCurrencyDecimal.toNumber(),
        ];
        return {
            name: ticker,
            ticker,
            decimals,
            stableLpToken: {
                poolId,
            },
        };
    }
    getOptimalTrade(inputAmount, outputCurrency, pools) {
        const pairs = amm_1.getAllTradingPairs(pools);
        if (pairs.length === 0 || inputAmount.isZero()) {
            return null;
        }
        return amm_1.findBestTradeRecursively(inputAmount, outputCurrency, pairs, HOP_LIMIT);
    }
    getLiquidityProvidedByAccount(accountId) {
        return __awaiter(this, void 0, void 0, function* () {
            const allLpTokens = yield this.getLpTokens();
            const accountBalances = yield Promise.all(allLpTokens.map((lpToken) => this.tokensAPI.balance(lpToken, accountId)));
            // Adds free and staked balances together.
            return accountBalances.map((balance) => balance.free.add(balance.reserved));
        });
    }
    _getStandardLpTokens() {
        return __awaiter(this, void 0, void 0, function* () {
            const standardPools = yield this.api.query.dexGeneral.liquidityPairs.entries();
            const standardLpTokens = yield Promise.all(standardPools.map(([_, lpTokenCurrencyId]) => utils_1.getStandardLpTokenFromCurrencyId(this.api, lpTokenCurrencyId.unwrap())));
            return standardLpTokens;
        });
    }
    _getStableLpTokens() {
        return __awaiter(this, void 0, void 0, function* () {
            const stablePools = yield this.api.query.dexStable.pools.entries();
            const stableLpTokens = stablePools.map(([key, poolData]) => {
                if (!poolData.isSome) {
                    return null;
                }
                const poolBase = DefaultAMMAPI.getStablePoolInfo(poolData.unwrap());
                if (poolBase === null) {
                    return null;
                }
                return DefaultAMMAPI.getStableLpTokenFromPoolData(utils_1.storageKeyToNthInner(key).toNumber(), poolBase);
            });
            return stableLpTokens.filter((token) => token !== null);
        });
    }
    getLpTokens() {
        return __awaiter(this, void 0, void 0, function* () {
            const [standardLpTokens, stableLpTokens] = yield Promise.all([
                this._getStandardLpTokens(),
                this._getStableLpTokens(),
            ]);
            return [...standardLpTokens, ...stableLpTokens];
        });
    }
    _poolHasZeroLiquidity(pooledCurrencies) {
        return pooledCurrencies.some((amount) => amount.isZero());
    }
    _getStandardPoolReserveBalances(token0, token1, pairAccount) {
        return __awaiter(this, void 0, void 0, function* () {
            const [token0Balance, token1Balance] = yield Promise.all([
                this.tokensAPI.balance(token0, pairAccount),
                this.tokensAPI.balance(token1, pairAccount),
            ]);
            const token0MonetaryAmount = token0Balance.free;
            const token1MonetaryAmount = token1Balance.free;
            return [token0MonetaryAmount, token1MonetaryAmount];
        });
    }
    _getPoolRewardAmountsYearly(lpTokenCurrencyId, blockTimeMs) {
        return __awaiter(this, void 0, void 0, function* () {
            const rewardPeriod = this.api.consts.farming.rewardPeriod;
            const rewardsRaw = yield this.api.query.farming.rewardSchedules.entries(lpTokenCurrencyId);
            const rewardAmountsYearly = yield Promise.all(rewardsRaw.map(([key, value]) => __awaiter(this, void 0, void 0, function* () {
                const rewardCurrencyId = utils_1.storageKeyToNthInner(key, 1);
                const rewardCurrency = yield utils_1.currencyIdToMonetaryCurrency(this.api, rewardCurrencyId);
                const amountPerBlock = big_js_1.default(value.perPeriod.toString()).div(rewardPeriod.toNumber());
                const annualizedRewardAmount = utils_1.calculateAnnualizedRewardAmount(amountPerBlock, blockTimeMs);
                return utils_1.newMonetaryAmount(annualizedRewardAmount, rewardCurrency);
            })));
            return rewardAmountsYearly;
        });
    }
    _getStandardLiquidityPool(pairCurrencies, lpTokenCurrencyId, pairStatus, blockTimeMs) {
        return __awaiter(this, void 0, void 0, function* () {
            let typedPairStatus;
            let isTradingActive;
            let tradingFee;
            let totalSupplyAmount;
            if (pairStatus.isTrading) {
                typedPairStatus = pairStatus.asTrading;
                isTradingActive = true;
                tradingFee = big_js_1.default(typedPairStatus.feeRate.toString()).div(FEE_MULTIPLIER_STANDARD);
                totalSupplyAmount = utils_1.decodeFixedPointType(typedPairStatus.totalSupply);
            }
            else if (pairStatus.isBootstrap) {
                typedPairStatus = pairStatus.asBootstrap;
                isTradingActive = false;
                tradingFee = big_js_1.default(0);
                totalSupplyAmount = big_js_1.default(0);
            }
            else {
                return null;
            }
            const pairAccount = typedPairStatus.pairAccount;
            const [token0, token1] = yield Promise.all(pairCurrencies.map((currency) => utils_1.currencyIdToMonetaryCurrency(this.api, currency)));
            const [lpToken, pooledCurrencies, yearlyRewards] = yield Promise.all([
                utils_1.getStandardLpTokenFromCurrencyId(this.api, lpTokenCurrencyId),
                this._getStandardPoolReserveBalances(token0, token1, pairAccount),
                this._getPoolRewardAmountsYearly(lpTokenCurrencyId, blockTimeMs),
            ]);
            // Do not include pools with zero liquidity.
            if (this._poolHasZeroLiquidity(pooledCurrencies)) {
                return null;
            }
            const totalSupply = new monetary_js_1.MonetaryAmount(lpToken, totalSupplyAmount);
            return new amm_1.StandardLiquidityPool(lpToken, pooledCurrencies, yearlyRewards, tradingFee, isTradingActive, totalSupply);
        });
    }
    getStandardLiquidityPools(blockTimeMs) {
        return __awaiter(this, void 0, void 0, function* () {
            const pairEntries = yield this.api.query.dexGeneral.liquidityPairs.entries();
            const pairs = pairEntries.filter(([_, lpToken]) => lpToken.isSome);
            const pairStatuses = yield Promise.all(pairs.map(([pairKey]) => this.api.query.dexGeneral.pairStatuses(utils_1.storageKeyToNthInner(pairKey))));
            const pools = yield Promise.all(pairs.map(([pairKey, lpToken], index) => this._getStandardLiquidityPool(utils_1.storageKeyToNthInner(pairKey), lpToken.unwrap(), pairStatuses[index], blockTimeMs)));
            return pools.filter((pool) => pool !== null);
        });
    }
    _getStablePoolPooledCurrencies(currencyIds, balances) {
        return __awaiter(this, void 0, void 0, function* () {
            const pooledMonetaryCurrencies = yield Promise.all(currencyIds.map((currencyId) => utils_1.currencyIdToMonetaryCurrency(this.api, currencyId)));
            const pooledCurrencies = pooledMonetaryCurrencies.map((currency, index) => utils_1.newMonetaryAmount(big_js_1.default(balances[index].toString()), currency));
            return pooledCurrencies;
        });
    }
    _getStablePoolAmplificationCoefficient(poolId) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            // TODO: refactor when RPC call is added to node
            // const rawA = await this.api.rpc.dexStable.getA(poolId);
            // return decodeNumberOrHex(rawA);
            const poolData = yield this.api.query.dexStable.pools(poolId);
            if (poolData.isSome) {
                const rawA = (_a = DefaultAMMAPI.getStablePoolInfo(poolData.unwrap())) === null || _a === void 0 ? void 0 : _a.futureA;
                if (rawA !== undefined) {
                    return big_js_1.default(rawA.toString());
                }
            }
            throw new Error(`_getStablePoolAmplificationCoefficient: Invalid pool id ${poolId}.`);
        });
    }
    _getStableBasePooledCurrenciesAdjustedToLpTokenAmount(basePooledCurrencies, lpTokenTotalSupply, metaPoolLpTokenAmount) {
        const changeCoefficient = metaPoolLpTokenAmount.div(lpTokenTotalSupply.toBig()).toBig();
        return basePooledCurrencies.map((amount) => amount.mul(changeCoefficient));
    }
    _getStableLiquidityPoolData(poolId, poolData, blockTimeMs, metaPoolLpTokenAmount) {
        return __awaiter(this, void 0, void 0, function* () {
            const poolInfo = DefaultAMMAPI.getStablePoolInfo(poolData);
            if (poolInfo === null) {
                return null;
            }
            const [pooledCurrencyIds, pooledCurrencyBalances, tradingFee, lpTokenCurrencyId] = [
                poolInfo.currencyIds,
                poolInfo.balances,
                big_js_1.default(poolInfo.fee.toString()).div(FEE_MULTIPLIER_STABLE),
                poolInfo.lpCurrencyId,
            ];
            const lpToken = DefaultAMMAPI.getStableLpTokenFromPoolData(poolId, poolInfo);
            const [pooledCurrenciesBase, yearlyRewards, amplificationCoefficient, totalSupply] = yield Promise.all([
                this._getStablePoolPooledCurrencies(pooledCurrencyIds, pooledCurrencyBalances),
                this._getPoolRewardAmountsYearly(lpTokenCurrencyId, blockTimeMs),
                this._getStablePoolAmplificationCoefficient(poolId),
                this.tokensAPI.total(lpToken),
            ]);
            // Adjust currency amounts based on LP token amount of metapool.
            const actuallyPooledCurrencies = metaPoolLpTokenAmount !== undefined
                ? this._getStableBasePooledCurrenciesAdjustedToLpTokenAmount(pooledCurrenciesBase, totalSupply, metaPoolLpTokenAmount)
                : pooledCurrenciesBase;
            return { lpToken, actuallyPooledCurrencies, yearlyRewards, amplificationCoefficient, totalSupply, tradingFee };
        });
    }
    _getStableMetaPoolBasePool(poolData, pooledCurrencies, blockTimeMs) {
        return __awaiter(this, void 0, void 0, function* () {
            const basePoolId = poolData.basePoolId;
            const basePoolData = yield this.api.query.dexStable.pools(basePoolId);
            const pooledLpTokenAmount = (pooledCurrencies.find((amount) => utils_1.isStableLpToken(amount.currency)));
            if (basePoolData.isSome) {
                if (basePoolData.unwrap().isMeta) {
                    throw new Error("Nested metapools are not supported.");
                }
                const basePool = yield this._getStableLiquidityPool(basePoolId.toNumber(), basePoolData.unwrap(), blockTimeMs, pooledLpTokenAmount);
                if (basePool === null) {
                    throw new Error("Metapool's base pool data are not valid.");
                }
                return basePool;
            }
            throw new Error(`Base pool with id ${basePoolId} does not exist.`);
        });
    }
    _getStableLiquidityPool(poolId, poolData, blockTimeMs, metaPoolLpTokenAmount) {
        return __awaiter(this, void 0, void 0, function* () {
            const processedPoolData = yield this._getStableLiquidityPoolData(poolId, poolData, blockTimeMs, metaPoolLpTokenAmount);
            if (processedPoolData === null) {
                return null;
            }
            const { lpToken, actuallyPooledCurrencies, yearlyRewards, tradingFee, amplificationCoefficient, totalSupply } = processedPoolData;
            // Do not include pools with zero liquidity.
            if (this._poolHasZeroLiquidity(actuallyPooledCurrencies)) {
                return null;
            }
            if (poolData.isBase) {
                return new amm_1.StableLiquidityPool(amm_1.PoolType.STABLE_PLAIN, lpToken, actuallyPooledCurrencies, actuallyPooledCurrencies, yearlyRewards, tradingFee, poolId, amplificationCoefficient, totalSupply);
            }
            // When pool is metapool, nested base pool instance is created.
            const basePool = yield this._getStableMetaPoolBasePool(poolData.asMeta, actuallyPooledCurrencies, blockTimeMs);
            const pooledCurrencies = actuallyPooledCurrencies.reduce((result, currentAmount) => utils_1.isStableLpToken(currentAmount.currency)
                ? [...result, ...basePool.pooledCurrencies]
                : [...result, currentAmount], []);
            return new amm_1.StableLiquidityMetaPool(lpToken, actuallyPooledCurrencies, pooledCurrencies, yearlyRewards, tradingFee, poolId, amplificationCoefficient, totalSupply, basePool);
        });
    }
    getStableLiquidityPools(blockTimeMs) {
        return __awaiter(this, void 0, void 0, function* () {
            const poolEntries = yield this.api.query.dexStable.pools.entries();
            const rawPoolsData = poolEntries.filter(([_, pool]) => pool.isSome);
            const pools = yield Promise.all(rawPoolsData.map(([poolId, poolData]) => this._getStableLiquidityPool(utils_1.storageKeyToNthInner(poolId).toNumber(), poolData.unwrap(), blockTimeMs)));
            return pools.filter((pool) => pool !== null);
        });
    }
    getLiquidityPools() {
        return __awaiter(this, void 0, void 0, function* () {
            const blockTimeMs = (yield this.api.call.auraApi.slotDuration()).toNumber();
            const [standardPools, stablePools] = yield Promise.all([
                this.getStandardLiquidityPools(blockTimeMs),
                this.getStableLiquidityPools(blockTimeMs),
            ]);
            return [...standardPools, ...stablePools];
        });
    }
    _swapThroughStandardPoolsOnly(trade, minimumAmountOut, recipient, deadline) {
        return __awaiter(this, void 0, void 0, function* () {
            const { amountIn, amountOutMin, path } = amm_1.encodeSwapParamsForStandardPoolsOnly(this.api, trade, minimumAmountOut);
            const swapExtrinsic = this.api.tx.dexGeneral.swapExactAssetsForAssets(amountIn, amountOutMin, path, utils_1.addressOrPairAsAccountId(this.api, recipient), deadline);
            yield this.transactionAPI.sendLogged(swapExtrinsic, this.api.events.dexGeneral.AssetSwap, true);
        });
    }
    _swapThroughStandardAndStablePools(trade, minimumAmountOut, recipient, deadline) {
        return __awaiter(this, void 0, void 0, function* () {
            const { amountIn, amountOutMin, path } = amm_1.encodeSwapParamsForStandardAndStablePools(this.api, trade, minimumAmountOut);
            const swapExtrinsic = this.api.tx.dexSwapRouter.swapExactTokenForTokensThroughStablePool(amountIn, amountOutMin, path, utils_1.addressOrPairAsAccountId(this.api, recipient), deadline);
            yield this.transactionAPI.sendLogged(swapExtrinsic, this.api.events.dexStable.CurrencyExchange, true);
        });
    }
    _getClaimableFarmingRewardsByPool(accountId, lpToken, pool) {
        return __awaiter(this, void 0, void 0, function* () {
            const lpTokenCurrencyId = utils_1.newCurrencyId(this.api, lpToken);
            const rewardCurrencyIds = pool.rewardAmountsYearly.map(({ currency: rewardCurrency }) => utils_1.newCurrencyId(this.api, rewardCurrency));
            const farmingRewards = yield Promise.all(rewardCurrencyIds.map((rewardCurrencyId) => this.api.rpc.reward.computeFarmingReward(accountId, lpTokenCurrencyId, rewardCurrencyId)));
            const rewardAmounts = pool.rewardAmountsYearly.map(({ currency: rewardCurrency }, index) => utils_1.newMonetaryAmount(farmingRewards[index].amount.toString(), rewardCurrency));
            return rewardAmounts;
        });
    }
    getClaimableFarmingRewards(accountId, accountLiquidity, pools) {
        return __awaiter(this, void 0, void 0, function* () {
            const rewardAmounts = yield Promise.all(accountLiquidity.map(({ currency }) => {
                const pool = pools.find((poolData) => utils_1.isCurrencyEqual(poolData.lpToken, currency));
                if (pool === undefined) {
                    // Return empty array for pools without liquidity.
                    return [];
                }
                return this._getClaimableFarmingRewardsByPool(accountId, currency, pool);
            }));
            const claimableRewards = new Map();
            rewardAmounts.forEach((rewards, index) => {
                const lpToken = accountLiquidity[index].currency;
                claimableRewards.set(lpToken, rewards);
            });
            return claimableRewards;
        });
    }
    swap(trade, minimumAmountOut, recipient, deadline) {
        return __awaiter(this, void 0, void 0, function* () {
            const containsStablePool = trade.path.some(amm_1.isStableMultiPathElement);
            if (containsStablePool) {
                yield this._swapThroughStandardAndStablePools(trade, minimumAmountOut, recipient, deadline);
            }
            else {
                yield this._swapThroughStandardPoolsOnly(trade, minimumAmountOut, recipient, deadline);
            }
        });
    }
    _getLiquidityDepositStandardPoolParams(amounts, pool, maxSlippageComplement, deadline) {
        return __awaiter(this, void 0, void 0, function* () {
            if (amounts.length !== 2) {
                throw new Error("Invalid count of input amounts.");
            }
            if (!utils_1.isCurrencyEqual(pool.token0, amounts[0].currency) || !utils_1.isCurrencyEqual(pool.token1, amounts[1].currency)) {
                throw new Error("Input currencies and pool currencies differ.");
            }
            const minAmounts = amounts.map((amount) => amount.mul(maxSlippageComplement));
            const [asset0, asset1, amount0Desired, amount1Desired, amount0Min, amount1Min] = [
                utils_1.newCurrencyId(this.api, amounts[0].currency),
                utils_1.newCurrencyId(this.api, amounts[1].currency),
                amounts[0].toString(true),
                amounts[1].toString(true),
                minAmounts[0].toString(true),
                minAmounts[1].toString(true),
            ];
            const addLiquidityToStandardPoolExtrinsic = this.api.tx.dexGeneral.addLiquidity(asset0, asset1, amount0Desired, amount1Desired, amount0Min, amount1Min, deadline);
            return [addLiquidityToStandardPoolExtrinsic, this.api.events.dexGeneral.LiquidityAdded];
        });
    }
    _getLiquidityDepositStablePoolParams(amounts, pool, maxSlippageComplement, deadline, recipient) {
        return __awaiter(this, void 0, void 0, function* () {
            const minAmounts = amounts.map((amount) => amount.mul(maxSlippageComplement));
            const minimumLpTokenOut = pool.getLiquidityDepositLpTokenAmount(minAmounts[0]).toString(true);
            const recipientAccount = utils_1.addressOrPairAsAccountId(this.api, recipient);
            if (!amm_1.isStableMetaPool(pool)) {
                amounts.forEach((amount, index) => {
                    if (!utils_1.isCurrencyEqual(pool.pooledCurrencies[index].currency, amount.currency)) {
                        throw new Error(`Invalid input amounts, currency ${amount.currency.ticker} is not at index ${index} of pool.`);
                    }
                });
                const rawAmounts = amounts.map(utils_1.monetaryAmountToRawString);
                const addLiquidityToStablePoolExtrinsic = this.api.tx.dexStable.addLiquidity(pool.poolId, rawAmounts, minimumLpTokenOut, recipientAccount, deadline);
                return [addLiquidityToStablePoolExtrinsic, this.api.events.dexStable.AddLiquidity];
            }
            // Pass 0 for LP token amount - this will be automatically changed base on how much
            // liquidity is really added to base pool.
            const metaAmounts = pool.actuallyPooledCurrencies.map((actuallyPooledCurrency) => amounts.find((amount) => utils_1.isCurrencyEqual(amount.currency, actuallyPooledCurrency.currency)) ||
                new monetary_js_1.MonetaryAmount(actuallyPooledCurrency.currency, 0));
            const rawMetaAmounts = metaAmounts.map(utils_1.monetaryAmountToRawString);
            const baseAmounts = amounts.filter((amount) => pool.basePool.involvesToken(amount.currency));
            const rawBaseAmounts = baseAmounts.map(utils_1.monetaryAmountToRawString);
            if (metaAmounts.length + baseAmounts.length !==
                pool.actuallyPooledCurrencies.length + pool.basePool.actuallyPooledCurrencies.length) {
                throw new Error("Invalid input amounts.");
            }
            const addLiquidityToStableMetaPoolExtrinsic = this.api.tx.dexStable.addPoolAndBasePoolLiquidity(pool.poolId, pool.basePool.poolId, rawMetaAmounts, rawBaseAmounts, minimumLpTokenOut, recipientAccount, deadline);
            return [addLiquidityToStableMetaPoolExtrinsic, this.api.events.dexStable.AddLiquidity];
        });
    }
    addLiquidity(amounts, pool, maxSlippage, deadline, recipient) {
        return __awaiter(this, void 0, void 0, function* () {
            const maxSlippageComplement = 1 - maxSlippage / 100;
            let depositExtrinsic;
            let depositEvent;
            if (amm_1.isStandardPool(pool)) {
                [depositExtrinsic, depositEvent] = yield this._getLiquidityDepositStandardPoolParams(amounts, pool, maxSlippageComplement, deadline);
            }
            else {
                [depositExtrinsic, depositEvent] = yield this._getLiquidityDepositStablePoolParams(amounts, pool, maxSlippageComplement, deadline, recipient);
            }
            const lpTokenCurrencyId = utils_1.newCurrencyId(this.api, pool.lpToken);
            const farmDepositExtrinsic = this.api.tx.farming.deposit(lpTokenCurrencyId);
            const batchedExtrinsics = this.api.tx.utility.batchAll([depositExtrinsic, farmDepositExtrinsic]);
            yield this.transactionAPI.sendLogged(batchedExtrinsics, depositEvent, true);
        });
    }
    _getLiquidityWithdrawalStandardPoolParams(amount, pool, maxSlippageComplement, recipient, deadline) {
        return __awaiter(this, void 0, void 0, function* () {
            const outputAmounts = pool.getLiquidityWithdrawalPooledCurrencyAmounts(amount);
            const minAmounts = outputAmounts.map((amount) => amount.mul(maxSlippageComplement).toString(true));
            const recipientAccount = utils_1.addressOrPairAsAccountId(this.api, recipient);
            const withdrawalExtrinsic = this.api.tx.dexGeneral.removeLiquidity(utils_1.newCurrencyId(this.api, pool.token0), utils_1.newCurrencyId(this.api, pool.token1), amount.toString(true), minAmounts[0], minAmounts[1], recipientAccount, deadline);
            return [withdrawalExtrinsic, this.api.events.dexGeneral.LiquidityRemoved];
        });
    }
    _getLiquidityWithdrawalStablePoolParams(amount, pool, maxSlippageComplement, recipient, deadline) {
        return __awaiter(this, void 0, void 0, function* () {
            const outputAmounts = pool.getLiquidityWithdrawalPooledCurrencyAmounts(amount);
            const minAmounts = outputAmounts.map((amount) => amount.mul(maxSlippageComplement));
            const poolId = pool.poolId;
            const lpTokenAmount = amount.toString(true);
            const recipientAccount = utils_1.addressOrPairAsAccountId(this.api, recipient);
            if (amm_1.isStableMetaPool(pool)) {
                const basePoolId = pool.basePool.poolId;
                const minAmountsMeta = pool.actuallyPooledCurrencies
                    .map((actuallyPooledCurrency) => minAmounts.find((amount) => utils_1.isCurrencyEqual(amount.currency, actuallyPooledCurrency.currency)) || new monetary_js_1.MonetaryAmount(actuallyPooledCurrency.currency, 0))
                    .map(utils_1.monetaryAmountToRawString);
                const minAmountsBase = minAmounts
                    .filter((amount) => pool.basePool.involvesToken(amount.currency))
                    .map(utils_1.monetaryAmountToRawString);
                const withdrawLiquidityExtrinsic = this.api.tx.dexStable.removePoolAndBasePoolLiquidity(poolId, basePoolId, lpTokenAmount, minAmountsMeta, minAmountsBase, recipientAccount, deadline);
                return [withdrawLiquidityExtrinsic, this.api.events.dexStable.RemoveLiquidity];
            }
            const minAmountsRaw = minAmounts.map(utils_1.monetaryAmountToRawString);
            const withdrawLiquidityExtrinsic = this.api.tx.dexStable.removeLiquidity(poolId, lpTokenAmount, minAmountsRaw, recipientAccount, deadline);
            return [withdrawLiquidityExtrinsic, this.api.events.dexStable.RemoveLiquidity];
        });
    }
    removeLiquidity(amount, pool, maxSlippage, // Percentage.
    deadline, recipient) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!utils_1.isCurrencyEqual(amount.currency, pool.lpToken)) {
                throw new Error(`Input amount and pool lp token should be same but are: [${amount.currency.ticker}, ${pool.lpToken.ticker}].`);
            }
            const maxSlippageComplement = 1 - maxSlippage / 100;
            let withdrawalExtrinsic;
            let withdrawalEvent;
            if (amm_1.isStandardPool(pool)) {
                [withdrawalExtrinsic, withdrawalEvent] = yield this._getLiquidityWithdrawalStandardPoolParams(amount, pool, maxSlippageComplement, recipient, deadline);
            }
            else {
                [withdrawalExtrinsic, withdrawalEvent] = yield this._getLiquidityWithdrawalStablePoolParams(amount, pool, maxSlippageComplement, recipient, deadline);
            }
            const lpTokenCurrencyId = utils_1.newCurrencyId(this.api, pool.lpToken);
            const farmWithdrawalExtrinsic = this.api.tx.farming.withdraw(lpTokenCurrencyId, amount.toString(true));
            const batchedExtrinsics = this.api.tx.utility.batchAll([farmWithdrawalExtrinsic, withdrawalExtrinsic]);
            yield this.transactionAPI.sendLogged(batchedExtrinsics, withdrawalEvent, true);
        });
    }
    claimFarmingRewards(claimableRewards) {
        return __awaiter(this, void 0, void 0, function* () {
            const claimExtrinsics = [];
            for (const [lpToken, rewards] of claimableRewards.entries()) {
                const lpTokenId = utils_1.newCurrencyId(this.api, lpToken);
                rewards.forEach((rewardAmount) => {
                    if (rewardAmount.toBig().gt(0)) {
                        const rewardCurrencyId = utils_1.newCurrencyId(this.api, rewardAmount.currency);
                        const claimExtrinsic = this.api.tx.farming.claim(lpTokenId, rewardCurrencyId);
                        claimExtrinsics.push(claimExtrinsic);
                    }
                });
            }
            const batchedExtrinsics = this.api.tx.utility.batchAll(claimExtrinsics);
            yield this.transactionAPI.sendLogged(batchedExtrinsics, this.api.events.farming.RewardClaimed, true);
        });
    }
}
exports.DefaultAMMAPI = DefaultAMMAPI;
//# sourceMappingURL=amm.js.map